<template>
  <Component
    :is="label ? 'label' : 'div'"
    class="text-area"
  >
    <div
      v-if="label"
      class="text-area__label"
    >
      {{ label }}
    </div>
    <textarea
      :id="id"
      v-model="inputText"
      class="text-area__input"
      type="textarea"
      :rows="rows"
      :placeholder="placeholder"
      :name="name"
      :required="required"
      :disabled="disabled"
      @change="update"
      @keyup.stop
      @keypress.stop
    />
  </Component>
</template>

<script lang="ts">
export default {
  name: 'TextArea',
}
</script>

<script setup lang="ts">
import { computed } from 'vue'

const props = // eslint-disable-next-line vue/define-props-declaration
  defineProps({
    disabled: { default: false, type: Boolean },
    required: { required: false, type: Boolean },
    modelValue: { required: true, type: String },
    label: { default: null, type: String },
    id: { default: null, type: String },
    rows: { default: null, type: String },
    placeholder: { default: null, type: String },
    name: { default: null, type: String },
  })

const emit = defineEmits<{
  (e: 'update:modelValue', val: string): void
}>()

const inputText = computed({
  get() {
    return props.modelValue
  },
  set(val: string) {
    emit('update:modelValue', val)
  },
})

const update = (e: Event | null): void => {
  if (!e || !e.target) {
    return
  }

  if (!('value' in e.target)) {
    return
  }

  if (!(typeof e.target.value === 'string')) {
    return
  }

  inputText.value = e.target.value
}
</script>

<style lang="scss" scoped>
$input-height: 33px;

.text-area {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;

  &__label {
    @include typographyRegularBody200;
    color: var(--color-text-subtle);
    margin-bottom: 4px;
  }

  &__input {
    @include typographyRegularBody200;
    color: var(--color-text-default);
    background-color: var(--color-background-transparent);

    width: 100%;
    height: 100%;
    border: none;
    padding: 15px 20px;
    box-shadow: none;
    border: 1px solid var(--color-border-input);
    resize: none;

    &:valid,
    &:focus,
    &:active {
      outline: none !important;
      border: 1.5px solid var(--color-border-focused);
      background-color: var(--color-surface-default);
    }

    &::placeholder {
      color: var(--color-text-subtle);
    }
  }
}
</style>
